import { Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ScreenreaderService {
    public liveMessage1 = signal<string>('');
    public liveMessage2 = signal<string>('');
    private currentRegion = 1;
    private messageQueue: Array<{ text: string; timestamp: number }> = [];
    private isProcessing = false;

    public screenReaderMessages(message): void {
        const messagesToAdd: string[] = [];
        let hasLinks = false;

        if (message.promptText) {
            let promptMessage = message.promptText;

            if (message.choices && message.choices.length > 0) {
                const choiceTexts = message.choices.map((choice) => `Knop: ${choice.text}`).join(', ');
                promptMessage += ` Keuze uit: ${choiceTexts}. Ga terug naar het chatgesprek om een keuze te maken.`;
            }

            const result = this.sanitizeAndSplitMessage(promptMessage);
            hasLinks = hasLinks || result.hasLinks;
            messagesToAdd.push(...result.messages);
        }

        if (message.text || message.introductionText) {
            const result = this.sanitizeAndSplitMessage(message.text || message.introductionText);
            hasLinks = hasLinks || result.hasLinks;
            messagesToAdd.push(...result.messages);
        }

        if (hasLinks && messagesToAdd.length > 0) {
            const suffix = '. Ga terug naar het chatgesprek om de link te openen.';
            const lastIndex = messagesToAdd.length - 1;
            messagesToAdd[lastIndex] = messagesToAdd[lastIndex].trim() + suffix;
        }

        messagesToAdd.forEach((msg) => {
            this.messageQueue.push({ text: msg, timestamp: Date.now() });
        });

        if (!this.isProcessing) {
            this.processQueue();
        }
    }

    private processQueue(): void {
        if (this.messageQueue.length === 0) {
            this.isProcessing = false;
            return;
        }

        this.isProcessing = true;
        const message = this.messageQueue.shift()!;

        if (this.currentRegion === 1) {
            this.liveMessage1.update(() => message.text);
            this.currentRegion = 2;
        } else {
            this.liveMessage2.update(() => message.text);
            this.currentRegion = 1;
        }

        const readingTime = this.calculateReadingTime(message.text);

        setTimeout(() => {
            this.processQueue();
        }, readingTime);
    }

    private calculateReadingTime(message: string): number {
        const wordCount = message.split(/\s+/).length;
        const msPerWord = 350;
        const baseTime = wordCount * msPerWord;
        const buffer = 1000;
        const minTime = 2500;
        const maxTime = 25000;

        return Math.min(maxTime, Math.max(minTime, baseTime + buffer));
    }

    private sanitizeAndSplitMessage(message: string): { messages: string[]; hasLinks: boolean } {
        const div = document.createElement('div');
        div.innerHTML = message;

        const messages: string[] = [];
        let hasLinks = false;

        const allLinks = div.querySelectorAll('a');
        if (allLinks.length > 0) {
            hasLinks = true;
        }

        allLinks.forEach((link) => {
            /* istanbul ignore next */
            const linkText = link.textContent || link.innerText;
            link.innerHTML = `, Link, ${linkText},`;
        });

        const images = div.querySelectorAll('img');
        images.forEach((image) => {
            const altText = image.alt && image.alt.trim() !== '' ? `, Afbeelding, ${image.alt}.` : ', Afbeelding, omschrijving ontbreekt.';
            const textNode = document.createTextNode(altText);
            image.parentNode?.replaceChild(textNode, image);
        });

        const lists = div.querySelectorAll('ul, ol');
        lists.forEach((list, listIndex) => {
            const listItems = list.querySelectorAll('li');

            if (listItems.length > 0) {
                const marker = `__LIST_${listIndex}_START__`;
                const listTexts: string[] = [];

                listTexts.push(`Lijst met ${listItems.length} items:`);
                listItems.forEach((item, index) => {
                    /* istanbul ignore next */
                    const itemText = item.textContent || item.innerText || '';
                    listTexts.push(`${index + 1}. ${itemText.trim()}`);
                });

                const markerNode = document.createTextNode(` ${marker} `);
                list.replaceWith(markerNode);

                (window as any)[`__listContent_${listIndex}`] = listTexts;
            }
        });

        const textContent = div.textContent || div.innerText || '';
        let processedContent = textContent.replace(/(\d+)\.(\S)/g, '$1. $2');
        processedContent = processedContent.replace(/([.!?])([A-Z])/g, '$1 $2');
        processedContent = processedContent.replace(/\s+/g, ' ').trim();
        processedContent = processedContent.replace(/ > /g, ', ');

        const parts = processedContent.split(/__LIST_\d+_START__/);
        const listMarkers = processedContent.match(/__LIST_(\d+)_START__/g) || [];

        parts.forEach((part, index) => {
            const trimmedPart = part.trim();
            if (trimmedPart) {
                messages.push(trimmedPart);
            }

            if (index < listMarkers.length) {
                const listIndex = listMarkers[index].match(/__LIST_(\d+)_START__/)![1];
                const listContent = (window as any)[`__listContent_${listIndex}`];
                messages.push(...listContent);
                delete (window as any)[`__listContent_${listIndex}`];
            }
        });

        return { messages, hasLinks };
    }

    public splitIntoEqualChunks(message: string, maxChunkLength: number): string[] {
        const protectedPhrases = [
            'ASN Bank',
            'ASN Online Bankieren',
            'Instant Payments',
            'Goede Vrijdag',
            'Dag van de Arbeid',
            'ASN creditcard'
        ];

        const sentenceBoundaries = ['. ', '! ', '? ', ': ', '; '];
        const listItemPatterns = [/^\d+\.\s/, /^•\s/, /^-\s/, /^–\s/, /^\*\s/];

        let tokenizedMessage = message;
        const tokenMap = new Map<string, string>();

        protectedPhrases.forEach((phrase, index) => {
            const token = `__PROTECTED_${index}__`;
            const regex = new RegExp(`\\b${phrase}\\b`, 'gi');
            tokenizedMessage = tokenizedMessage.replace(regex, token);
            tokenMap.set(token, phrase);
        });

        const sentences = [];
        let currentSentence = '';
        const words = tokenizedMessage.split(/\s+/);

        for (let i = 0; i < words.length; i++) {
            const word = words[i];
            const nextWord = i < words.length - 1 ? words[i + 1] : '';

            currentSentence += (currentSentence.length > 0 ? ' ' : '') + word;

            const isEndOfSentence = sentenceBoundaries.some((boundary) => word.endsWith(boundary.trim()));
            const isListItem = listItemPatterns.some((pattern) => nextWord.match(pattern));

            if (isEndOfSentence || isListItem || i === words.length - 1) {
                sentences.push(currentSentence);
                currentSentence = '';
            }
        }

        const chunks: string[] = [];
        let currentChunk = '';

        for (const sentence of sentences) {
            if (currentChunk.length + sentence.length + 1 > maxChunkLength && currentChunk.length > 0) {
                chunks.push(currentChunk.trim());
                currentChunk = sentence;
            } else {
                currentChunk += (currentChunk.length > 0 ? ' ' : '') + sentence;
            }
        }

        if (currentChunk.length > 0) {
            chunks.push(currentChunk.trim());
        }

        return chunks.map((chunk) => {
            let restoredChunk = chunk;
            tokenMap.forEach((phrase, token) => {
                restoredChunk = restoredChunk.replace(new RegExp(token, 'g'), phrase);
            });
            return restoredChunk;
        });
    }
}
